import React from 'react';
import { useDispatch } from 'react-redux';
import { Button, Grid, Typography } from '@mui/material';
import { Form } from 'formik';
import * as Yup from 'yup';
import { Notification } from 'components/notification/models/Notification';
import { notificationApi } from 'components/notification/notificationApi';
import {
    MESSAGE_CUSTOMER_CONTACT_ERROR,
    MESSAGE_CUSTOMER_CONTACT_SKIPPED,
    MESSAGE_CUSTOMER_CONTACT_SUCCESS
} from 'config/messages';
import { MuiForm } from 'lib/Form';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { Row } from 'ui/Flex';
import { enqueueSnackbar } from 'store/notifications/notificationsActions';
import { Customer } from '../models/Customer';

interface CustomerContactFormProps {
    onClose: () => void;
    customer: Customer;
}

const initialValues = {
    message: ''
};

const validationSchema = Yup.object().shape({
    message: Yup.string().required('A message is required.')
});

export const CustomerContactForm: React.FC<CustomerContactFormProps> = props => {
    const { onClose, customer } = props;
    const [isLoading, setIsLoading] = React.useState(false);
    const dispatch = useDispatch();

    const checkForNotificationResult = React.useCallback(
        async (notification: Notification<string>) => {
            try {
                // Wait for operation to complete in some mannar
                const result = await notificationApi.pollNotification({ notification });
                setIsLoading(false);
                if (result.skipped) {
                    dispatch(enqueueSnackbar(MESSAGE_CUSTOMER_CONTACT_SKIPPED, { variant: 'warning' }));
                    return onClose();
                }
                if (result.sent) {
                    dispatch(enqueueSnackbar(MESSAGE_CUSTOMER_CONTACT_SUCCESS, { variant: 'success' }));
                    return onClose();
                }
                return dispatch(enqueueSnackbar(MESSAGE_CUSTOMER_CONTACT_ERROR, { variant: 'error' }));
            } catch (err) {
                setIsLoading(false);
                dispatch(enqueueSnackbar(MESSAGE_CUSTOMER_CONTACT_ERROR, { variant: 'error' }));
            }
        },
        [dispatch, onClose]
    );

    const handleSubmit = React.useCallback(
        async ({ message }: { message: string }) => {
            setIsLoading(true);
            const type = 'alert';
            const isProcessingMessage = false;
            const value = '';
            const result = await notificationApi.send(customer._id, {
                body: { message, value, type, isProcessingMessage }
            });
            checkForNotificationResult(result.body);
        },
        [checkForNotificationResult, customer._id]
    );

    return (
        <MuiForm onSubmit={handleSubmit} initialValues={initialValues} validationSchema={validationSchema}>
            {({ submitForm, isValid, isSubmitting }) => (
                <Form>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h6">{`Contact ${customer.firstName}`}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2">
                                From here, you can send a message to {customer.firstName}. The message will
                                appear as a push notification on their phone.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextFormField
                                name="message"
                                label="Message"
                                multiline
                                rows={4}
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Row gutter align="flex-end">
                                <Button onClick={onClose} variant="outlined">
                                    Cancel
                                </Button>
                                <LoadingButton
                                    onClick={submitForm}
                                    disabled={!isValid || isSubmitting}
                                    loading={isLoading || isSubmitting}
                                    variant="contained"
                                >
                                    Send
                                </LoadingButton>
                            </Row>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </MuiForm>
    );
};
